"use client";

import * as React from "react";
import { isValidElement, ReactNode } from "react";
import { cva, VariantProps } from "class-variance-authority";
import { Check, ChevronDown, ChevronUp } from "lucide-react";
import * as SelectPrimitive from "@radix-ui/react-select"; // ⬅️ use official package
import { cn } from "../lib/utils";

// ---------- Portal context (per Select instance) ----------
type PortalContainer = ShadowRoot | HTMLElement | undefined;
type PortalCtxValue = {
  container: PortalContainer;
  setContainer: (el: PortalContainer) => void;
};
const PortalCtx = React.createContext<PortalCtxValue | null>(null);

function usePortalCtx() {
  const ctx = React.useContext(PortalCtx);
  if (!ctx) throw new Error("Select components must be used within <Select>");
  return ctx;
}

function detectContainerFrom(el: HTMLElement | null): PortalContainer {
  if (!el) return undefined;
  const root = el.getRootNode();
  return root instanceof ShadowRoot ? root : undefined; // undefined => default body
}

// Compose refs helper
function composeRefs<T>(...refs: Array<React.Ref<T> | undefined>) {
  return (node: T) => {
    for (const ref of refs) {
      if (!ref) continue;
      if (typeof ref === "function") ref(node);
      else {
        try {
          // @ts-ignore
          ref.current = node;
        } catch {}
      }
    }
  };
}

// ---------- Context to pass indicator options ----------
const SelectContext = React.createContext<{
  indicatorPosition: "left" | "right";
  indicatorVisibility: boolean;
  indicator: ReactNode;
}>({ indicatorPosition: "left", indicator: null, indicatorVisibility: true });

// ---------- Root (provides portal container state) ----------
const Select = ({
  indicatorPosition = "left",
  indicatorVisibility = true,
  indicator,
  ...props
}: {
  indicatorPosition?: "left" | "right";
  indicatorVisibility?: boolean;
  indicator?: ReactNode;
} & React.ComponentProps<typeof SelectPrimitive.Root>) => {
  const [container, setContainer] = React.useState<PortalContainer>(undefined);
  const portalValue = React.useMemo(
    () => ({ container, setContainer }),
    [container]
  );

  return (
    <PortalCtx.Provider value={portalValue}>
      <SelectContext.Provider
        value={{ indicatorPosition, indicatorVisibility, indicator }}
      >
        <SelectPrimitive.Root {...props} />
      </SelectContext.Provider>
    </PortalCtx.Provider>
  );
};

function SelectGroup({
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Group>) {
  return <SelectPrimitive.Group data-slot="select-group" {...props} />;
}

function SelectValue({
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Value>) {
  return <SelectPrimitive.Value data-slot="select-value" {...props} />;
}

// ---------- Trigger: detect container and write to context ----------
export interface SelectTriggerProps
  extends React.ComponentProps<typeof SelectPrimitive.Trigger>,
    VariantProps<typeof selectTriggerVariants> {}

const selectTriggerVariants = cva(
  `
    flex bg-background w-full items-center justify-between outline-none border border-input shadow-xs shadow-black/5 transition-shadow 
    text-foreground data-placeholder:text-muted-foreground focus-visible:border-ring focus-visible:outline-none focus-visible:ring-[3px] 
    focus-visible:ring-ring/30 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1 
    aria-invalid:border-destructive/60 aria-invalid:ring-destructive/10 dark:aria-invalid:border-destructive dark:aria-invalid:ring-destructive/20
    [[data-invalid=true]_&]:border-destructive/60 [[data-invalid=true]_&]:ring-destructive/10  dark:[[data-invalid=true]_&]:border-destructive dark:[[data-invalid=true]_&]:ring-destructive/20
  `,
  {
    variants: {
      size: {
        sm: "h-7 px-2.5 text-xs gap-1 rounded-md",
        md: "h-8.5 px-3 text-2sm leading-(--text-sm--line-height) gap-1 rounded-md",
        lg: "h-10 px-4 text-sm gap-1.5 rounded-md"
      }
    },
    defaultVariants: { size: "md" }
  }
);

const SelectTrigger = React.forwardRef<HTMLButtonElement, SelectTriggerProps>(
  function SelectTrigger(
    { className, children, size, ...props },
    forwardedRef
  ) {
    const { setContainer } = usePortalCtx();
    const localRef = React.useRef<HTMLButtonElement>(null);

    React.useLayoutEffect(() => {
      setContainer(detectContainerFrom(localRef.current));
    }, [setContainer]);

    const handlePointerDown: React.PointerEventHandler<HTMLButtonElement> = (
      e
    ) => {
      setContainer(detectContainerFrom(e.currentTarget as HTMLElement));
      props.onPointerDown?.(e);
    };

    return (
      <SelectPrimitive.Trigger
        ref={composeRefs(forwardedRef, localRef)}
        data-slot="select-trigger"
        className={cn(selectTriggerVariants({ size }), className)}
        onPointerDown={handlePointerDown}
        {...props}
      >
        {children}
        <SelectPrimitive.Icon asChild>
          <ChevronDown className="h-4 w-4 opacity-60 -me-0.5" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
    );
  }
);

// ---------- Scroll buttons ----------
function SelectScrollUpButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollUpButton>) {
  return (
    <SelectPrimitive.ScrollUpButton
      data-slot="select-scroll-up-button"
      className={cn(
        "flex cursor-default items-center justify-center py-1",
        className
      )}
      {...props}
    >
      <ChevronUp className="h-4 w-4" />
    </SelectPrimitive.ScrollUpButton>
  );
}

function SelectScrollDownButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownButton>) {
  return (
    <SelectPrimitive.ScrollDownButton
      data-slot="select-scroll-down-button"
      className={cn(
        "flex cursor-default items-center justify-center py-1",
        className
      )}
      {...props}
    >
      <ChevronDown className="h-4 w-4" />
    </SelectPrimitive.ScrollDownButton>
  );
}

// ---------- Portal wrapper using context ----------
function SelectPortalWithShadow({
  children
}: React.ComponentProps<typeof SelectPrimitive.Portal>) {
  const { container } = usePortalCtx();
  return (
    <SelectPrimitive.Portal
      // TS doesn’t accept ShadowRoot here even though it works at runtime
      container={(container as unknown as HTMLElement) ?? undefined}
    >
      {children}
    </SelectPrimitive.Portal>
  );
}

// ---------- Content: always portal via the contextual container ----------
function SelectContent({
  className,
  children,
  position = "popper",
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Content>) {
  return (
    <SelectPortalWithShadow>
      <SelectPrimitive.Content
        data-slot="select-content"
        className={cn(
          "relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-md border border-border bg-popover shadow-md shadow-black/5 text-secondary-foreground",
          "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
          "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
          position === "popper" &&
            "data-[side=bottom]:translate-y-1.5 data-[side=left]:-translate-x-1.5 data-[side=right]:translate-x-1.5 data-[side=top]:-translate-y-1.5",
          className
        )}
        position={position}
        {...props}
      >
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport
          className={cn(
            "p-1.5",
            position === "popper" &&
              "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]"
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPortalWithShadow>
  );
}

function SelectLabel({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Label>) {
  return (
    <SelectPrimitive.Label
      data-slot="select-label"
      className={cn(
        "py-1.5 ps-8 pe-2 text-xs text-muted-foreground font-medium",
        className
      )}
      {...props}
    />
  );
}

// ---------- Items / indicator ----------
function SelectItem({
  className,
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Item>) {
  const { indicatorPosition, indicatorVisibility, indicator } =
    React.useContext(SelectContext);

  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={cn(
        "relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 text-sm outline-hidden text-foreground hover:bg-accent focus:bg-accent data-disabled:pointer-events-none data-disabled:opacity-50",
        indicatorPosition === "left" ? "ps-8 pe-2" : "pe-8 ps-2",
        className
      )}
      {...props}
    >
      {indicatorVisibility &&
        (indicator && isValidElement(indicator) ? (
          indicator
        ) : (
          <span
            className={cn(
              "absolute flex h-3.5 w-3.5 items-center justify-center",
              indicatorPosition === "left" ? "start-2" : "end-2"
            )}
          >
            <SelectPrimitive.ItemIndicator>
              <Check className="h-4 w-4 text-primary" />
            </SelectPrimitive.ItemIndicator>
          </span>
        ))}
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
}

function SelectIndicator({
  children,
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ItemIndicator>) {
  const { indicatorPosition } = React.useContext(SelectContext);

  return (
    <span
      data-slot="select-indicator"
      className={cn(
        "absolute flex top-1/2 -translate-y-1/2 items-center justify-center",
        indicatorPosition === "left" ? "start-2" : "end-2",
        className
      )}
      {...props}
    >
      <SelectPrimitive.ItemIndicator>{children}</SelectPrimitive.ItemIndicator>
    </span>
  );
}

function SelectSeparator({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Separator>) {
  return (
    <SelectPrimitive.Separator
      data-slot="select-separator"
      className={cn("-mx-1.5 my-1.5 h-px bg-border", className)}
      {...props}
    />
  );
}

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectIndicator,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue
};
